import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SearchBar from "@/components/feature/SearchBar";
import FavoritesDrawer from "@/components/feature/FavoritesDrawer";
import { useFavorites } from "@/hooks/useFavorites";
import { allCategories } from "@/lib/catalog";

const navItems = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category/classic" },
  { label: "资讯", to: "/news" },
  { label: "搜索", to: "/search" },
];

export default function SiteHeader() {
  const location = useLocation();
  const { ids } = useFavorites();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!categoryOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && !target.closest("[data-category-menu]")) setCategoryOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [categoryOpen]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.split("/").slice(0, 2).join("/"));
  };

  return (
    <>
      <div className="w-full border-b border-background-300/40 bg-background-100">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-[11px] text-foreground-500 md:px-8 font-label">
          <p className="flex items-center gap-2">
            <i className="ri-information-line text-accent-500" />
            演示站点：影片、人物与评分均为虚构示例，仅提供文字资料，不提供任何播放服务
          </p>
          <p className="hidden items-center gap-2 sm:flex">
            <span className="flex h-4 items-center rounded-full border border-primary-500/70 px-2 text-[10px] text-primary-300">
              18+
            </span>
            内容面向成年观众
          </p>
        </div>
      </div>

      <header className="sticky top-0 z-50 w-full border-b border-background-300/60 bg-background-50/95 backdrop-blur-md">
        <div className="flex h-16 w-full items-center gap-4 px-4 md:h-20 md:px-8">
          <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center border border-accent-500/70 font-heading text-lg font-bold text-accent-500 md:h-11 md:w-11">
              情
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-[15px] font-bold tracking-wide text-foreground-50 md:text-base">
                日本情色电影
              </span>
              <span className="block text-[9px] tracking-widest2 text-foreground-500 font-label">
                JAPAN EROTIC CINEMA ARCHIVE
              </span>
            </span>
          </Link>

          <nav className="ml-6 hidden h-full items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) =>
              item.label === "分类" ? (
                <div key={item.label} className="relative h-full" data-category-menu>
                  <button
                    type="button"
                    onClick={() => setCategoryOpen((open) => !open)}
                    className={`flex h-full cursor-pointer items-center gap-1 px-3 text-[13px] transition-colors font-label ${
                      isActive("/category") ? "text-accent-400" : "text-foreground-300 hover:text-accent-400"
                    }`}
                    aria-expanded={categoryOpen}
                  >
                    分类
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
                  </button>
                  {categoryOpen && (
                    <div className="absolute left-0 top-full w-[460px] border border-background-300/70 bg-background-100 p-2">
                      <div className="grid grid-cols-2 gap-1">
                        {allCategories.map((category) => (
                          <Link
                            key={category.slug}
                            to={`/category/${category.slug}`}
                            className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-200/70"
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-900/50 text-accent-400">
                              <i className={`${category.icon} text-sm`} />
                            </span>
                            <span className="min-w-0">
                              <span className="block font-heading text-[13px] text-foreground-100">{category.name}</span>
                              <span className="mt-0.5 block truncate text-[11px] text-foreground-500">{category.en}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`flex h-full cursor-pointer items-center px-3 text-[13px] transition-colors font-label ${
                    isActive(item.to) ? "text-accent-400" : "text-foreground-300 hover:text-accent-400"
                  }`}
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          <div className="ml-auto hidden w-[240px] md:block xl:w-[280px]">
            <SearchBar />
          </div>

          <button
            type="button"
            onClick={() => setFavoritesOpen(true)}
            className="relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
            aria-label="打开收藏夹"
          >
            <i className="ri-bookmark-line text-sm" />
            {ids.length > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-foreground-50 font-label">
                {ids.length}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="打开菜单"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-200 lg:hidden"
          >
            <i className="ri-menu-3-line" />
          </button>
        </div>
      </header>

      <div className={`fixed inset-0 z-[58] lg:hidden ${mobileOpen ? "" : "pointer-events-none"}`}>
        <button
          type="button"
          aria-label="关闭菜单"
          onClick={() => setMobileOpen(false)}
          className={`absolute inset-0 cursor-pointer bg-background-50/85 backdrop-blur-sm transition-opacity duration-300 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          className={`absolute left-0 top-0 h-full w-[86%] max-w-[360px] overflow-y-auto border-r border-background-300/70 bg-background-100 transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between border-b border-background-300/60 px-5 py-4">
            <span className="font-heading text-sm font-bold text-foreground-50">菜单</span>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="关闭"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-300"
            >
              <i className="ri-close-line" />
            </button>
          </div>

          <div className="px-5 py-4">
            <SearchBar />
          </div>

          <nav className="px-3" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="flex cursor-pointer items-center justify-between rounded-md px-3 py-3 text-[13px] text-foreground-200 transition-colors hover:bg-background-200/60 hover:text-accent-400 font-label"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-500" />
              </Link>
            ))}
          </nav>

          <div className="mt-2 border-t border-background-300/50 px-5 py-4">
            <p className="text-[10px] tracking-widest2 text-accent-500 font-label">CATEGORIES</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {allCategories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="flex cursor-pointer flex-col gap-1 rounded-md border border-background-300/60 px-3 py-3 transition-colors hover:border-accent-500/60"
                >
                  <i className={`${category.icon} text-base text-accent-400`} />
                  <span className="font-heading text-[12px] text-foreground-100">{category.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <FavoritesDrawer open={favoritesOpen} onClose={() => setFavoritesOpen(false)} />
    </>
  );
}